import { Card, mq, Divider } from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { CollapsiblePanel } from "./CollapsiblePanel";
import { Header } from "./Header";
import { isComplianceReviewHoldStatus } from "../review-hold/utils";
import {
  checkResultHasUiElementsOrMessages,
  hasExternalFulfillmentNotes,
  shouldOpenCheckResultOnPdf,
  shouldShowDataWhenInProgress,
} from "./utils";
import { CheckResult, ReportData, AllChecksFulfillmentNotes } from "../types";

interface CheckResultCardProps {
  isChildCheck?: boolean;
  checkResult: CheckResult;
  isPDF?: boolean;
  allCheckResults: CheckResult[];
  applicantDetails: Pick<
    ReportData["applicant_info"],
    "date_of_birth" | "current_name"
  >;
  allChecksFulfillmentNotes: AllChecksFulfillmentNotes;
}

export function CheckResultCard(props: CheckResultCardProps) {
  const { t } = useTranslation();
  const {
    isChildCheck = false,
    isPDF = false,
    checkResult,
    applicantDetails,
    allCheckResults,
    allChecksFulfillmentNotes,
  } = props;
  const { type_display, status } = checkResult;
  const styles = getStyles(isChildCheck);
  const fulfillmentNotes = allChecksFulfillmentNotes.find(
    (elem) => elem.check_id === checkResult.id
  );
  const hasFulfillmentNotes = hasExternalFulfillmentNotes(fulfillmentNotes);
  const [openResult, setOpenResult] = useState<boolean | undefined>(() => {
    const hasChildren = Boolean(checkResult.child_checks?.length);
    if (hasChildren) {
      return true;
    }
    if (!isPDF) {
      return (
        status === "IN_PROGRESS" &&
        hasFulfillmentNotes &&
        shouldShowDataWhenInProgress(checkResult.type)
      );
    }
    return shouldOpenCheckResultOnPdf({
      status,
      hasResults: checkResultHasUiElementsOrMessages(checkResult),
      canShowInProgress: shouldShowDataWhenInProgress(checkResult.type),
      hasFulfillmentNotes,
    });
  });

  useEffect(() => {
    const hasResults = checkResultHasUiElementsOrMessages(checkResult);
    const canShowInProgress = shouldShowDataWhenInProgress(checkResult.type);
    const hasChildren = Boolean(checkResult.child_checks?.length);
    if (
      (isPDF &&
        shouldOpenCheckResultOnPdf({
          status,
          hasResults,
          canShowInProgress,
          hasFulfillmentNotes,
        })) ||
      (!isPDF && isComplianceReviewHoldStatus(status)) ||
      hasChildren ||
      (!isPDF &&
        status === "IN_PROGRESS" &&
        hasFulfillmentNotes &&
        canShowInProgress)
    )
      setOpenResult(true);
  }, [isPDF, status, checkResult, hasFulfillmentNotes]);

  if (!props) {
    return null;
  }

  return (
    <Card
      hideBorder={isPDF || isChildCheck}
      type="large"
      ariaLabel={t("{{type_display}} Section Details", {
        type_display,
      })}
      className={styles.cardContainer}
      testID="check-result-card"
    >
      <Header
        openResult={openResult}
        checkResult={checkResult}
        isPDF={isPDF}
        setOpenResult={setOpenResult}
        isChildCheck={isChildCheck}
        fulfillmentNotes={fulfillmentNotes}
      />
      {openResult && (
        <CollapsiblePanel
          checkResult={checkResult}
          fulfillmentNotes={fulfillmentNotes}
          isPDF={isPDF}
        />
      )}
      {checkResult.child_checks?.map((child_check) => {
        return (
          <div key={child_check.id}>
            <Divider />
            <CheckResultCard
              isChildCheck
              checkResult={child_check}
              isPDF={isPDF}
              key={child_check.id}
              allCheckResults={allCheckResults}
              applicantDetails={applicantDetails}
              allChecksFulfillmentNotes={allChecksFulfillmentNotes}
            />
          </div>
        );
      })}
    </Card>
  );
}

const getStyles = (isChildCheck: boolean) => ({
  cardContainer: css({
    marginBottom: "var(--space-4)",
    background: isChildCheck ? "none" : "var(--background-base)",
    // Print: each check starts a new page, except when this card is the first
    // child of the report card. That happens for partial PDFs (exclude_overview),
    // where there is no overview node in front — skipping the break avoids a
    // blank page 1. Do not insert wrappers before the check list, and do not
    // switch this to :first-of-type (overview is a div; check cards are sections).
    "@media print": {
      pageBreakBefore: "always",
      "&:first-child": { pageBreakBefore: "auto" },
    },
  }),
  container: css(
    mq({
      margin: "var(--space-4)",
      "@media print": { marginLeft: 0, marginRight: 0 },
    })
  ),
  header: css({ display: "flex", justifyContent: "space-between" }),
  checkResult: css({
    display: "flex",
    alignItems: "center",
    gap: "var(--space-2)",
  }),
});
